import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import styled from 'styled-components';
import { FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaGlobe, FaBars, FaExclamationCircle } from 'react-icons/fa';
import { setcqlData } from '../features/layers/cqlDataSlice';
import { useSelector, useDispatch } from "react-redux";
import { setAnalyticsDetails, selectLayerDataSet } from '../features/layers/overlaylayerslice';

const Header = () => {
  const dispatch = useDispatch();
  const overlayLayers = useSelector(selectLayerDataSet);
  
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('Asia');
  const [expanded, setExpanded] = useState(false);
  const [maxDate, setMaxDate] = useState('');
  const [minDate, setMinDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const dateInputRef = useRef(null);

  // Helper to find the active model layer
  const activeLayer = overlayLayers?.find(layer => layer.show && layer.class === "Model");

  // Initialize Min/Max Date constraints once
  useEffect(() => {
    const today = new Date();
    const threeDaysAgo = new Date(today);
    threeDaysAgo.setDate(today.getDate() - 2);
    
    setMaxDate(today.toISOString().split('T')[0]);
    setMinDate(threeDaysAgo.toISOString().split('T')[0]);
  }, []);

  // 1. Handle Layer Switching Logic
  useEffect(() => {
    if (!activeLayer) return;

    const setupInitialDate = async () => {
      const today = new Date().toISOString().split('T')[0];

      if (activeLayer.datatype === 'wbgt') {
        // For WBGT: Default to today immediately
        setSelectedDate(today);
        dispatch(setAnalyticsDetails({
          id: activeLayer.id - 1,
          odate: today,
          date: `${today}-hr-00-00-00`
        }));
      } else {
        // For Others: Check API for latest available
        const isTodayAvailable = await checkDateAvailability(today);
        const finalDate = isTodayAvailable ? today : getYesterday();
        
        setSelectedDate(finalDate);
        dispatch(setAnalyticsDetails({
          id: activeLayer.id - 1,
          odate: finalDate,
          date: `${finalDate}-hr-00-00-00`
        }));
      }
    };

    setupInitialDate();
  }, [activeLayer?.id, activeLayer?.datatype]); // Only re-run when the layer itself changes

  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  };

  const checkDateAvailability = async (dateString) => {
    try {
      const response = await fetch('https://api.ncmrwf.gov.in/checkAvailabilityData/');
      if (!response.ok) return false;
      const data = await response.json();
      return data.all_folders_exist;
    } catch (error) {
      return false;
    }
  };

  const handleDateChange = async (event) => {
    const newDate = event.target.value;
    if (!newDate || !activeLayer) return;

    if (activeLayer.datatype === 'wbgt') {
      // Allow any date change for WBGT without checking API
      setSelectedDate(newDate);
      dispatch(setAnalyticsDetails({
        id: activeLayer.id - 1,
        odate: newDate,
        date: `${newDate}-hr-00-00-00`
      }));
    } else {
      // Perform validation for other layers
      const isAvailable = await checkDateAvailability(newDate);
      if (isAvailable) {
        setSelectedDate(newDate);
        dispatch(setAnalyticsDetails({
          id: activeLayer.id - 1,
          odate: newDate,
          date: `${newDate}-hr-00-00-00`
        }));
      } else {
        setShowModal(true);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedLocation) toast.info(`Searching: ${selectedLocation.display_name}`);
  };

  const handleLocationSearch = async (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!val.trim()) return setSuggestions([]);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${val}&limit=5`);
      const data = await res.json();
      setSuggestions(data);
    } catch (err) { console.error(err); }
  };

  const handleSelectLocation = (loc) => {
    setQuery(loc.display_name);
    setSuggestions([]);
    setSelectedLocation(loc);
    dispatch(setcqlData({ lat: loc.lat, lng: loc.lon, show: 'yes' }));
  };

  const handleRegionChange = (e) => {
    const region = e.target.value;
    setSelectedRegion(region);
    const coords = {
      Asia: { lat: 20.5937, lon: 78.9629 },
      Europe: { lat: 54.5260, lon: 15.2551 },
      Africa: { lat: 1.6500, lon: 10.2679 },
      'North America': { lat: 37.0902, lon: -95.7129 },
      'South America': { lat: -8.7832, lon: -55.4915 },
      Australia: { lat: -25.2744, lon: 133.7751 },
    }[region];
    dispatch(setcqlData({ lat: coords.lat, lng: coords.lon, show: 'yes1' }));
  };

  const handleCloseModal = () => setShowModal(false);

  return (
    <HeaderWrapper>
      <ToastContainer />
      <Navbar variant="dark" expand="lg" expanded={expanded}>
        <Container fluid className="header-container">
          <Navbar.Brand className="title">
            National Centre For Medium Range Weather Forecasting
          </Navbar.Brand>
          
          <Navbar.Toggle 
            aria-controls="basic-navbar-nav" 
            onClick={() => setExpanded(expanded ? false : true)}
            className="toggle-button"
          >
            <FaBars />
          </Navbar.Toggle>
          
          <Navbar.Collapse id="basic-navbar-nav">
            <form className="search-bar" onSubmit={handleSearch}>
              <div className="search-input">
                <FaSearch className="icon" />
                <input
                  type="text"
                  placeholder="Search location"
                  value={query}
                  onChange={handleLocationSearch}
                />
                {suggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {suggestions.map((location, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleSelectLocation(location)}
                      >
                        <FaMapMarkerAlt className="icon" />
                        <span>{location.display_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="controls-container">
                <div className="control-item region-picker">
                  <FaGlobe className="icon" />
                  <select value={selectedRegion} onChange={handleRegionChange}>
                    <option value="Asia">Asia</option>
                    <option value="Europe">Europe</option>
                    <option value="Africa">Africa</option>
                    <option value="North America">N America</option>
                    <option value="South America">S America</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>

                <div className="control-item date-picker" onClick={() => dateInputRef.current?.showPicker()}>
                  <FaCalendarAlt className="icon" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={minDate}
                    max={maxDate}
                    ref={dateInputRef}
                    className="date-input"
                  />
                  <span className="date-text">
                    {selectedDate || 'Select Date'}
                  </span>
                </div>
              </div>
            </form>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Data Not Available Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered className="data-unavailable-modal">
        <Modal.Body>
          <div className="modal-content-wrapper">
            <div className="modal-icon-container">
              <FaExclamationCircle className="modal-icon" />
            </div>
            <h3 className="modal-title">Data Not Available</h3>
            <div className="modal-message">
              <p><strong>Data for the selected date is currently unavailable</strong></p>
              <p>Please check back after some time. Our systems are processing the latest weather data.</p>
            </div>
            <div className="modal-divider"></div>
            <div className="modal-actions">
              <Button 
                variant="primary" 
                onClick={handleCloseModal}
                className="understand-btn"
              >
                UNDERSTAND
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </HeaderWrapper>
  );
};

export default Header;

const HeaderWrapper = styled.div`
  .navbar {
    background-color: #033969db;
    min-height: 50px;
    position: relative;
    padding: 0.3rem 0.5rem;
  }

  .header-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    gap: 0.5rem;
  }

  .title {
    font-size: 0.9rem;
    font-weight: bold;
    color: white;
    margin-right: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    flex-shrink: 1;
  }

  .toggle-button {
    border: none;
    background: transparent;
    color: white;
    font-size: 1.2rem;
    padding: 0.3rem;
    margin-left: auto;
  }

  .navbar-collapse {
    flex-grow: 0;
    width: 100%;
    margin-top: 0.3rem;
  }

  .search-bar {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    width: 100%;
    position: relative;
  }

  .search-input, .control-item {
    display: flex;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 0.25rem 0.4rem;
    height: 32px;
    box-sizing: border-box;
    transition: all 0.2s ease;
  }

  .search-input:hover, .control-item:hover {
    background-color: white;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }

  .search-input {
    position: relative;
    width: 100%;
  }

  .icon {
    font-size: 12px;
    color: #033969;
    margin-right: 0.3rem;
    flex-shrink: 0;
  }

  .search-input input {
    border: none;
    padding: 0.2rem;
    outline: none;
    width: 100%;
    font-size: 0.8rem;
    height: 100%;
    background: transparent;
    color: #033969;
  }

  .search-input input::placeholder {
    color: rgba(3, 57, 105, 0.6);
  }

  .suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1px solid rgba(3, 57, 105, 0.2);
    border-radius: 4px;
    max-height: 180px;
    overflow-y: auto;
    z-index: 9999;
    margin-top: 0.2rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .suggestion-item {
    padding: 0.4rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    cursor: pointer;
    font-size: 0.8rem;
    color: #033969;
    border-bottom: 1px solid rgba(3, 57, 105, 0.1);
  }

  .suggestion-item:last-child {
    border-bottom: none;
  }

  .suggestion-item:hover {
    background-color: rgba(3, 57, 105, 0.05);
  }

  .controls-container {
    display: flex;
    gap: 0.4rem;
    width: 100%;
  }

  .control-item {
    flex: 1;
    min-width: 0;
    position: relative;
  }

  .region-picker {
    display: flex;
    align-items: center;
  }

  .region-picker select {
    border: none;
    outline: none;
    font-size: 0.8rem;
    width: 100%;
    background: transparent;
    height: 100%;
    padding: 0;
    color: #033969;
    cursor: pointer;
    appearance: none;
    padding-right: 1.2rem;
  }

  .region-picker::after {
    content: "";
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #033969;
    pointer-events: none;
  }

  .date-picker {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .date-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .date-text {
    margin-left: 5px;
    font-size: 0.8rem;
    color: #033969;
  }

  /* Professional Modal Styles */
  .data-unavailable-modal .modal-content {
    border-radius: 8px;
    border: none;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .data-unavailable-modal .modal-body {
    padding: 2rem;
  }

  .modal-content-wrapper {
    text-align: center;
  }

  .modal-icon-container {
    margin-bottom: 1.2rem;
  }

  .modal-icon {
    font-size: 2.5rem;
    color: #dc3545;
  }

  .modal-title {
    color: #212529;
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.2rem;
  }

  .modal-message {
    color: #6c757d;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .modal-message p {
    margin-bottom: 0.8rem;
  }

  .modal-message p:last-child {
    margin-bottom: 0;
  }

  .modal-message strong {
    color: #212529;
    font-weight: 600;
  }

  .modal-divider {
    height: 1px;
    background-color: #e9ecef;
    margin: 1.5rem 0;
  }

  .modal-actions {
    display: flex;
    justify-content: center;
  }

  .understand-btn {
    background-color: #033969;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1.5rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
  }

  .understand-btn:hover {
    background-color: #02294d;
    transform: translateY(-1px);
  }

  @media (min-width: 576px) {
    .title {
      font-size: 1rem;
    }
    .navbar {
      padding: 0.3rem 0.8rem;
    }
  }

  @media (min-width: 768px) {
    .title {
      font-size: 1.1rem;
    }
    
    .navbar-collapse {
      width: auto;
      margin-top: 0;
    }
    
    .search-bar {
      flex-direction: row;
      align-items: center;
      width: auto;
      gap: 0.5rem;
    }
    
    .search-input {
      width: 180px;
    }
    
    .controls-container {
      width: auto;
      gap: 0.5rem;
    }
    
    .control-item {
      width: 130px;
    }
  }

  @media (min-width: 992px) {
    .navbar {
      padding: 0.3rem 1rem;
    }
    
    .title {
      font-size: 1.2rem;
    }
    
    .search-input {
      width: 200px;
    }
    
    .control-item {
      width: 140px;
    }

    .icon {
      font-size: 13px;
    }
  }

  @media (max-width: 400px) {
    .title {
      font-size: 0.85rem;
    }
    
    .data-unavailable-modal .modal-body {
      padding: 1.5rem;
    }
    
    .modal-title {
      font-size: 1.3rem;
    }
  }
`;